import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { useActiveCell } from '@/hooks/useActiveCell';
import { captureWorkbookSnapshotCached, isCellEditingModeError, isCellTypingMode, type CapturedImage } from '@/services/office';
import { streamChat, type ChatStreamController } from '@/services/chat';
import { applyDiffMessage } from '@/services/applyFlow';
import { listModels } from '@/services/models';
import { generateSuggestions } from '@/services/suggestions';
import { extractErrorMessage } from '@/services/api';
import type { UsagePayload, WorkbookContext } from '@/types/chat';
import type { ModelsResponse } from '@/types/api';
import { MessageList } from './MessageList';
import { InputBox, type InputBoxHandle } from './InputBox';
import { PendingDiffBar } from './PendingDiffBar';
import type { Template } from './Templates';

export function ChatInterface() {
  const {
    provider, model, conversationId,
    streaming, appendUserMessage, startAssistantStream,
    appendAssistantDelta, attachToolCall, attachToolResult,
    attachDiffDelta, attachDiff, attachClarify, resolveClarify,
    finalizeAssistant, failAssistant,
    setThinking, setPhase, setConversation,
    setSuggestions,
    setMessageFinishReason, setAutoBuild, setApplyProgress, setPendingAutoContinue,
    autoBuild, applyProgress, pendingAutoContinue, autoBuildStopSeq,
    retrySeq, retryContent,
  } = useChatStore();

  // phaseFromTool maps a tool's facade name to the loading-animation
  // phase. Read tools → "reading"; cell/sheet/format/etc writes →
  // "writing"; charts and pivots → "creating"; web tools → "searching";
  // analysis + stats → "computing". Anything else (ask_clarification,
  // unknown) falls back to thinking. Single map, no provider-specific
  // branches — kept here so the SSE handler stays small.
  const phaseFromTool = (toolName: string): import('@/stores/chatStore').LoadingPhase => {
    switch (toolName) {
      case 'read_workbook':
        return 'reading';
      case 'cell_op':
      case 'structure_op':
      case 'sheet_op':
      case 'format_op':
      case 'data_op':
      case 'annotate_op':
        return 'writing';
      case 'visual_op':
        return 'creating';
      case 'web_op':
        return 'searching';
      case 'analysis_op':
      case 'compute_stats':
        return 'computing';
      default:
        return 'thinking';
    }
  };

  const activeContext = useActiveCell();
  const controllerRef = useRef<ChatStreamController | null>(null);
  const lastUsageRef = useRef<UsagePayload | undefined>(undefined);
  const inputRef = useRef<InputBoxHandle | null>(null);
  const [modelsData, setModelsData] = useState<ModelsResponse | null>(null);

  // --- Auto-Build (1-click dashboard) loop control --------------------------
  // All in refs (not state): SSE handler closures capture values at send()
  // time, so reading state here would be stale. Refs are always current.
  const MAX_AUTO_ROUNDS = 6;
  const autoActiveRef = useRef(false);          // is an auto-build chain running?
  const autoRoundsRef = useRef(0);              // continue rounds sent
  const autoCancelledRef = useRef(false);       // Stop / hard error
  const applyingRef = useRef(false);            // re-entrancy lock (one apply at a time)
  const lastAppliedMessageIdRef = useRef<string | null>(null); // idempotency
  const autoSignalRef = useRef<AbortController | null>(null);   // Stop checkpoint for applyChanges

  // tearDownAutoBuild fully halts an auto-build chain: cancels, aborts the
  // in-flight apply, releases the re-entrancy/idempotency locks, and clears
  // all UI state. Used by Stop, stream error, capture failure, and reject.
  const tearDownAutoBuild = () => {
    autoCancelledRef.current = true;
    autoActiveRef.current = false;
    applyingRef.current = false;
    autoSignalRef.current?.abort();
    autoSignalRef.current = null;
    setAutoBuild(null);
    setApplyProgress(null);
    setPendingAutoContinue(null);
  };

  // Load the model catalog once so we can answer "does current model
  // support vision?" without pinging the server per keystroke.
  useEffect(() => {
    listModels().then(setModelsData).catch(() => setModelsData(null));
  }, []);

  // ChatInterface unmounts when the user switches tabs (Settings). The
  // stream uses openWhenHidden, so without this cleanup it stays open with
  // no reachable controller — its events keep mutating the store, and a
  // NEW turn started after remount receives the OLD turn's text/diff.
  // Abort on unmount, and mark a live turn failed so the UI offers Retry
  // instead of a perpetual spinner.
  useEffect(() => {
    return () => {
      tearDownAutoBuild();
      controllerRef.current?.abort();
      controllerRef.current = null;
      const store = useChatStore.getState();
      if (store.streaming) {
        store.failAssistant('Interrupted — the chat was closed mid-response. Retry to continue.');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry-after-failure: MessageBubble bumps retrySeq; we re-send the
  // stored content as a brand-new turn (fresh idempotency key inside
  // streamChat). Guard against firing on mount (retrySeq starts at 0).
  const lastRetrySeqRef = useRef(0);
  useEffect(() => {
    if (retrySeq === 0 || retrySeq === lastRetrySeqRef.current) return;
    lastRetrySeqRef.current = retrySeq;
    if (retryContent && !useChatStore.getState().streaming) {
      void send(retryContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrySeq]);

  const visionSupported = useMemo(() => {
    if (!modelsData) return false;
    const list = modelsData.models?.[provider] ?? [];
    return list.find((m) => m.id === model)?.supports_vision ?? false;
  }, [modelsData, provider, model]);

  const onTemplatePick = useCallback((t: Template) => {
    inputRef.current?.setText(t.prompt);
  }, []);

  const send = async (msg: string, images?: CapturedImage[], attachmentIDs?: string[], isAutoContinue = false) => {
    // A fresh user prompt (NOT the auto-build's own "continue") starts a
    // clean slate. ABORT any orphaned in-flight apply (don't just drop the
    // controller) and release every lock, so an old chain can't write into
    // the new turn or fire a rogue continuation. Keyed on the explicit
    // isAutoContinue flag — not the message text — so a user literally
    // typing "continue" still resets cleanly.
    if (!isAutoContinue) {
      autoActiveRef.current = false;
      autoCancelledRef.current = false;
      autoRoundsRef.current = 0;
      autoSignalRef.current?.abort();
      autoSignalRef.current = null;
      applyingRef.current = false;
      lastAppliedMessageIdRef.current = null;
      setAutoBuild(null);
      setApplyProgress(null);
      setPendingAutoContinue(null);
    }
    appendUserMessage(msg);
    startAssistantStream();
    // Animation-only loading. The "Reading your workbook…" string is
    // retained for screen readers / telemetry but the visible UI shows
    // the LoadingPulse animation in the matching phase. Default phase
    // for the snapshot-capture window is "reading" since that's
    // literally what's happening on the client.
    setThinking('Reading your workbook…');
    setPhase('reading');
    lastUsageRef.current = undefined;

    // Capture the full workbook snapshot once per turn — richer than
    // the active-cell poll so tools have something to inspect.
    //
    // captureWorkbookSnapshotCached memoises within a 5-second TTL +
    // hash check, so rapid follow-ups ("ab bold kar", "title bada karo")
    // skip the 100-300ms Excel.run() walk entirely. The cached path is
    // automatically invalidated on selection-change events wired in the
    // useActiveContext hook.
    //
    // If the user is mid-edit in a cell, Office.js rejects every API
    // call with "cell-editing mode" — we detect that up front and abort
    // with a friendly message rather than letting the backend hallucinate
    // against an empty snapshot.
    let snapshot: WorkbookContext = {};
    let workbookHash = '';
    try {
      const cap = await captureWorkbookSnapshotCached();
      snapshot = cap.snapshot;
      workbookHash = cap.hash;
    } catch (err) {
      if (isCellEditingModeError(err)) {
        // A capture failure here ends the turn WITHOUT opening a stream, so
        // onDone never fires. If an auto-build chain is running, tear it
        // down now or the "Building dashboard…" strip wedges forever.
        if (autoActiveRef.current) tearDownAutoBuild();
        // Cell-typing mode = user mid-edit inside a cell; no API exists
        // to exit it programmatically. Chart/object selected case is
        // already auto-recovered inside captureWorkbookSnapshotCached,
        // so reaching here for a non-typing error means recovery failed.
        if (isCellTypingMode(err)) {
          failAssistant('Cell edit mode active hai — Enter ya Escape press karke exit karo, phir query bhejo.');
          toast.error('Cell mein type ho raha hai — Enter/Escape press karo.');
        } else {
          failAssistant('Workbook capture nahi ho paaya — kisi empty cell pe click karke dobara bhejo.');
          toast.error('Selection issue — empty cell pe click karke retry karo.');
        }
        return;
      }
      // Other capture failures: continue with empty snapshot; agent will
      // call read_workbook tools if it needs data.
      console.warn('workbook capture failed:', err);
    }
    // Merge: full snapshot may miss the *latest* selection if the user
    // moved the cursor between captures; overlay the active context.
    const workbookContext = { ...snapshot, ...activeContext };

    controllerRef.current = streamChat(
      {
        message: msg,
        conversation_id: conversationId ?? undefined,
        workbook_hash: workbookHash || undefined,
        workbook_context: workbookContext,
        images: images?.map((i) => ({ media_type: i.mediaType, data: i.data })),
        attachment_ids: attachmentIDs,
      },
      {
        onThinking: (p) => {
          // Backend "thinking" events arrive between iterations as the
          // model deliberates. We KEEP these for telemetry (thinkingText)
          // but route the visible UI through loadingPhase so the
          // animation reflects the agent's stage, not a free-text label.
          setThinking(p.message);
          setPhase('thinking');
        },
        // Reasoning deltas come from the model's extended-thinking
        // stream AND from the agent's internal "self-correcting…",
        // "verifying proposal…" status updates. Animation-only path:
        // the raw text never reaches the DOM — phase stays whatever
        // the most recent tool_call (or thinking event) set it to.
        onReasoning: (p) => {
          if (p.text && p.text.trim() !== '') {
            setThinking(p.text.trim());
          }
        },
        onText: (p) => {
          // Final user-facing text starts streaming → kill the
          // animation; the message bubble itself replaces it.
          setThinking(null);
          setPhase(null);
          appendAssistantDelta(p.chunk);
        },
        onToolCall: (p) => {
          attachToolCall(p);
          // Tool cards render below; show the matching animation
          // while the tool runs server- or client-side.
          setPhase(phaseFromTool(p.name));
          setThinking(p.name);
        },
        onToolResult: (p) => {
          attachToolResult(p);
          // Tool finished — animation defaults back to thinking
          // until the next tool_call or final text arrives.
          setPhase('thinking');
        },
        onDiffDelta: (p) => attachDiffDelta(p),
        onDiffReady: (p) => attachDiff(p),
        onClarify: (p) => {
          attachClarify(p);
          setThinking(null);
          setPhase(null);
        },
        onUsage: (p) => {
          lastUsageRef.current = p;
          if (p.credits_remaining !== undefined) {
            useAuthStore.getState().updateCredits(p.credits_remaining);
          }
        },
        onDone: (p) => {
          finalizeAssistant(lastUsageRef.current, p.message_id);
          lastUsageRef.current = undefined;
          setConversation(p.conversation_id);
          // Record finish_reason on the message so the manual Apply can
          // decide whether to start the auto-build chain.
          if (p.message_id) setMessageFinishReason(p.message_id, p.finish_reason);
          // Continuation rounds (chain already armed) auto-apply + maybe
          // continue. Round 1 is NOT auto — the user applies it manually.
          if (autoActiveRef.current && !autoCancelledRef.current && p.message_id) {
            void runAutoApplyAndMaybeContinue(p.message_id, p.conversation_id, p.finish_reason);
          }
          // Fire suggestions asynchronously. We read the assistant text
          // from the latest store snapshot because finalizeAssistant has
          // just merged the streamed chunks into it. Errors / empty
          // arrays just mean no chips — never toast.
          const finalId = p.message_id;
          if (finalId) {
            const snapshot = useChatStore.getState().messages;
            const assistant = snapshot.find((x) => x.id === finalId);
            const userIdx = snapshot.findIndex((x) => x.id === finalId) - 1;
            const user = userIdx >= 0 ? snapshot[userIdx] : undefined;
            if (assistant?.content && user?.content) {
              generateSuggestions({
                provider,
                last_user_message: user.content,
                last_assistant_msg: assistant.content,
              })
                .then((items) => {
                  if (items.length > 0) setSuggestions(finalId, items);
                })
                .catch(() => {
                  /* silent — chips are a polish feature */
                });
            }
          }
        },
        onError: (p) => {
          // Abort the underlying SSE so a half-open stream can't hang
          // after a server-sent error. failAssistant salvages any
          // partially-streamed diff to 'pending' so the work stays usable.
          // Any stream error is a HARD stop for an auto-build chain.
          tearDownAutoBuild();
          controllerRef.current?.abort();
          failAssistant(p.message || 'Chat failed');
          toast.error(p.message ?? 'Chat failed');
        },
        onClose: () => {
          // Server/proxy closed the stream WITHOUT a `done` event (idle
          // timeout, deploy restart, nginx cut). If we did receive `done`,
          // streaming is already false and this is a no-op. Otherwise the
          // bubble would spin forever with no error and no Retry.
          if (useChatStore.getState().streaming) {
            tearDownAutoBuild();
            setThinking(null);
            setPhase(null);
            failAssistant('Connection closed before the response finished — Retry to continue.');
          }
        },
      },
    );
  };

  const cancel = () => {
    // Stop halts the auto-build chain (aborts the in-flight apply +
    // releases locks) BEFORE aborting the stream.
    tearDownAutoBuild();
    controllerRef.current?.abort();
    failAssistant('Cancelled by user');
  };

  // send is re-bound every render (it closes over provider/model/context).
  // Route the MessageList callbacks through a ref so they can stay
  // referentially STABLE across the per-token re-renders of this
  // component — otherwise a fresh closure per token would defeat
  // MessageBubble's React.memo for the entire transcript.
  const sendRef = useRef(send);
  sendRef.current = send;

  const onClarifyPick = useCallback((messageId: string, interpretation: string) => {
    resolveClarify(messageId, interpretation);
    void sendRef.current(interpretation);
    // resolveClarify is a zustand action — stable for the store's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuggestionPick = useCallback((text: string) => {
    inputRef.current?.setText(text);
  }, []);

  // runAutoApplyAndMaybeContinue is the auto-build loop body, invoked from
  // onDone on each CONTINUATION round. It auto-applies the round's diff
  // (progressively) and, if the agent again stopped at the budget cap,
  // sends another "continue" — until a clean finish, the round cap, an
  // error, a reject, or a Stop.
  const runAutoApplyAndMaybeContinue = async (finalId: string, convId: string, finishReason?: string) => {
    if (applyingRef.current) return;                          // re-entrancy guard
    if (lastAppliedMessageIdRef.current === finalId) return;  // idempotency: never re-apply same message
    const msg = useChatStore.getState().messages.find((m) => m.id === finalId);
    if (!msg?.diff || msg.diff.status !== 'pending') {
      // No applicable diff this round → stop the chain cleanly.
      autoActiveRef.current = false;
      setAutoBuild(null);
      return;
    }

    applyingRef.current = true;
    setAutoBuild({ active: true, phase: 'applying', round: autoRoundsRef.current, max: MAX_AUTO_ROUNDS });
    const res = await applyDiffMessage(convId, msg, {
      stepDelayMs: 300,
      signal: autoSignalRef.current?.signal,
      onProgress: (done, total, label) => setApplyProgress({ messageId: finalId, done, total, label }),
    });
    setApplyProgress(null);
    lastAppliedMessageIdRef.current = finalId;
    applyingRef.current = false;

    // Stop on any failure, cancel, a fresh-prompt teardown that flipped
    // autoActive off, an aborted signal, or once the cap / clean finish is
    // reached. Re-checking autoActiveRef + the signal AFTER the await closes
    // the race where a new user prompt arrived while this apply was running.
    if (!res.ok || autoCancelledRef.current || !autoActiveRef.current || autoSignalRef.current?.signal.aborted) {
      autoActiveRef.current = false;
      setAutoBuild(null);
      return;
    }
    if (finishReason === 'partial_budget' && autoRoundsRef.current < MAX_AUTO_ROUNDS) {
      autoRoundsRef.current += 1;
      setAutoBuild({ active: true, phase: 'continuing', round: autoRoundsRef.current, max: MAX_AUTO_ROUNDS });
      toast.info('Continuing…');
      await send('continue', undefined, undefined, true); // re-captures workbook fresh → next round lands in onDone
    } else {
      autoActiveRef.current = false;
      setAutoBuild(null); // clean finish or cap reached
    }
  };

  // Arm the auto-build chain when a manual Apply of a partial_budget turn
  // sets pendingAutoContinue. Fires the FIRST "continue"; subsequent rounds
  // are driven by onDone → runAutoApplyAndMaybeContinue.
  useEffect(() => {
    if (!pendingAutoContinue) return;
    setPendingAutoContinue(null);
    autoActiveRef.current = true;
    autoCancelledRef.current = false;
    autoRoundsRef.current = 1;
    lastAppliedMessageIdRef.current = null;
    autoSignalRef.current = new AbortController();
    setAutoBuild({ active: true, phase: 'continuing', round: 1, max: MAX_AUTO_ROUNDS });
    toast.info('Continuing…');
    void send('continue', undefined, undefined, true);
    // send is stable enough; we only act on the pendingAutoContinue edge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoContinue]);

  // A reject (or any cross-component stop) bumps autoBuildStopSeq → tear
  // down the orchestrator refs that those components can't reach directly.
  useEffect(() => {
    if (autoBuildStopSeq === 0) return;
    tearDownAutoBuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBuildStopSeq]);

  const messageCount = useChatStore((s) => s.messages.length);
  const userCredits = useAuthStore((s) => s.user?.credits);

  return (
    <div className="flex h-full flex-col">
      {/* Session status strip — visible while streaming or after at
          least one turn. No token counts — dev telemetry, not user info. */}
      {(messageCount > 0 || streaming || userCredits !== undefined) && (
        <div className="flex items-center gap-3 border-b border-border bg-surface px-2 py-1 text-[10px] text-text-secondary dark:border-border-dark dark:bg-surface-dark">
          {streaming && (
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              working…
            </span>
          )}
          {userCredits !== undefined && (
            <span
              className={
                userCredits <= 0
                  ? 'text-red-500 font-medium'
                  : userCredits <= 1000
                  ? 'text-amber-500'
                  : ''
              }
              title={
                userCredits <= 0
                  ? 'Credits khatam — admin se contact karo'
                  : userCredits <= 1000
                  ? 'Credits kam hain'
                  : 'Remaining credits'
              }
            >
              {userCredits.toLocaleString()} credits
            </span>
          )}
          {/* Long-chat hint keys on MESSAGE COUNT, not token usage: one
              agentic build turn alone consumes 100K+ tokens, so a token
              threshold fired on the very first message (prod bug). What
              actually slows replies is a long back-and-forth history. */}
          {messageCount >= 40 && (
            <span
              className={messageCount >= 70 ? 'text-red-500 font-medium' : 'text-amber-500'}
              title="Long conversations slow responses down — a new chat starts fresh and fast."
            >
              {messageCount >= 70 ? 'Long chat — start a new one for faster replies' : 'Chat getting long'}
            </span>
          )}
        </div>
      )}

      <MessageList
        onClarifyPick={onClarifyPick}
        onTemplatePick={onTemplatePick}
        onSuggestionPick={onSuggestionPick}
      />

      {/* Auto-Build status strip — while the 1-click chain runs, show
          progress + a Stop button, and SUPPRESS the manual PendingDiffBar
          so the user can't race the auto-apply. */}
      {autoBuild?.active ? (
        <div className="flex items-center justify-between gap-2 border-t border-primary/30 bg-primary/5 px-3 py-2 dark:bg-primary/10 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="font-medium">
              {autoBuild.phase === 'continuing'
                ? `Building dashboard… (round ${autoBuild.round} of ${autoBuild.max})`
                : applyProgress
                  ? `Applying ${applyProgress.done} of ${applyProgress.total}… ${applyProgress.label}`
                  : 'Applying…'}
            </span>
          </div>
          <button type="button" onClick={cancel} className="btn-ghost text-[11px] px-2 py-1">
            Stop
          </button>
        </div>
      ) : (
        /* Sticky action bar — Apply / Reject sit just above the input box
           whenever there's a pending diff. */
        <PendingDiffBar conversationId={conversationId} />
      )}

      <InputBox
        ref={inputRef}
        onSend={(m, images, attachmentIDs) => {
          try {
            send(m, images, attachmentIDs);
          } catch (e) {
            toast.error(extractErrorMessage(e, 'Could not start chat'));
          }
        }}
        onCancel={cancel}
        streaming={streaming}
        workbookContext={activeContext}
        visionSupported={visionSupported}
      />
    </div>
  );
}
